import { useEffect, useRef, useState, type RefObject } from 'react'
import Link from 'next/link'
import type { attribute, discountPrice } from 'src/shared/frontendTypes/product'
import type { selectedAttributes } from './AttributesSelector'
import type { DisabledAttributeOptions } from '../../../functions/attributeAvailability'
import PriceDisplay from 'src/components/PriceDisplay'
import styles from './StickyPurchase.module.css'

interface Props {
  anchor: RefObject<HTMLDivElement>
  productId: number
  name: string
  price?: number | discountPrice
  attributes: attribute[]
  selectedAttributes: selectedAttributes
  disabledOptions: DisabledAttributeOptions
  onChangeAttribute: (attribute: number, option: number | '') => void
  quantity: number
  onEdit: () => void
  onAction: () => void
  actionLabel: string
  missingAttributes: boolean
  unavailable: boolean
  inquiry: boolean
  added: boolean
  suspended: boolean
  onlyStock?: boolean
  stock?: number
}

export default function StickyPurchase({
  anchor, name, price, attributes, selectedAttributes,
  quantity, onEdit, onAction, actionLabel, missingAttributes,
  unavailable, inquiry, added, suspended, onlyStock, stock,
}: Props) {
  const [outsideView, setOutsideView] = useState(false)
  const bar = useRef<HTMLDivElement>(null)
  const hasPrice = price !== undefined

  useEffect(() => {
    const target = anchor.current
    if (!target) return
    const header = document.querySelector('header')
    let observer: IntersectionObserver | undefined
    let observedHeaderHeight = -1
    const measure = () => {
      const headerHeight = header?.getBoundingClientRect().height || 0
      const rect = target.getBoundingClientRect()
      // Keep a purchase action available before AND after the main form.
      // Hide the duplicate only while the complete original action is visible.
      setOutsideView(window.innerWidth < 960 && (rect.top < headerHeight || rect.bottom > window.innerHeight))
      if (typeof IntersectionObserver !== 'undefined' && observedHeaderHeight !== Math.ceil(headerHeight)) {
        observedHeaderHeight = Math.ceil(headerHeight)
        observer?.disconnect()
        observer = new IntersectionObserver(([entry]) => {
          setOutsideView(window.innerWidth < 960 && (!entry.isIntersecting || entry.intersectionRatio < .99))
        }, { rootMargin: `-${Math.ceil(headerHeight)}px 0px 0px 0px`, threshold: [0, .99, 1] })
        observer.observe(target)
      }
    }
    measure()
    const headerObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    if (header) headerObserver?.observe(header)
    window.addEventListener('resize', measure)
    // Old browsers still keep the existing purchase form fully usable.
    if (typeof IntersectionObserver === 'undefined') window.addEventListener('scroll', measure, { passive: true })
    return () => {
      observer?.disconnect()
      headerObserver?.disconnect()
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure)
    }
  }, [anchor, inquiry, hasPrice])

  const visible = outsideView && !suspended
  useEffect(() => {
    if (!bar.current) return
    const root = document.documentElement
    const bodyPadding = document.body.style.paddingBottom
    const scrollPadding = root.style.scrollPaddingBottom
    const previousHeight = root.style.getPropertyValue('--product-purchase-bar-height')
    const basePadding = parseFloat(getComputedStyle(document.body).paddingBottom) || 0
    const measure = () => {
      // Reserve the mobile bar's space for the whole product visit. Changing
      // document height whenever the main button enters view causes scroll jumps.
      const height = window.innerWidth < 960 ? bar.current?.getBoundingClientRect().height || 0 : 0
      if (!height) {
        document.body.style.paddingBottom = bodyPadding
        root.style.scrollPaddingBottom = scrollPadding
        if (previousHeight) root.style.setProperty('--product-purchase-bar-height', previousHeight)
        else root.style.removeProperty('--product-purchase-bar-height')
        return
      }
      document.body.style.paddingBottom = `${basePadding + height}px`
      root.style.scrollPaddingBottom = `${height + 16}px`
      root.style.setProperty('--product-purchase-bar-height', `${height}px`)
    }
    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(bar.current)
    window.addEventListener('resize', measure)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', measure)
      document.body.style.paddingBottom = bodyPadding
      root.style.scrollPaddingBottom = scrollPadding
      if (previousHeight) root.style.setProperty('--product-purchase-bar-height', previousHeight)
      else root.style.removeProperty('--product-purchase-bar-height')
    }
  }, [])

  const choices = attributes.map(attribute => attribute.options.find(option => option.id === selectedAttributes[attribute.id])?.name).filter(Boolean)
  const selectionLabel = missingAttributes
    ? attributes.filter(attribute => typeof selectedAttributes[attribute.id] !== 'number').map(attribute => attribute.name).join(' & ') + ' wählen'
    : choices.join(' · ')

  return <div ref={bar} className={`${styles.bar} ${!visible ? styles.hidden : ''}`} role='region' aria-label='Produktauswahl und Warenkorb' aria-hidden={!visible || undefined} data-product-sticky-purchase>
    <div className={styles.inner}>
      <div className={styles.price}>
        {price !== undefined ? <>
          <div className={styles.amount}>{missingAttributes && <span>ab </span>}<PriceDisplay price={price} layout='compact' component='strong' variant='inherit' onlyStock={onlyStock} stock={stock} /></div>
          <span className={styles.tax}>pro Stück · inkl. MwSt.<br /><Link href='/zahlung-und-versand'>zzgl. Versand*¹</Link></span>
        </> : <strong>Preis auf Anfrage</strong>}
        {(!inquiry || attributes.length > 0) && <button type='button' className={styles.selection} onClick={onEdit} aria-haspopup='dialog' aria-label={`Auswahl ändern: ${selectionLabel || name}${inquiry ? '' : `, Menge ${quantity}`}`}>
          {missingAttributes ? 'Auswahl ergänzen' : inquiry ? selectionLabel : `${quantity} × ${selectionLabel || name}`} <span aria-hidden='true'>⌄</span>
        </button>}
      </div>
      <div className={styles.action}>
        {added && !missingAttributes && !inquiry ? <>
          <span className={styles.success} role='status'>Zum Warenkorb hinzugefügt</span>
          <Link href='/warenkorb' className={styles.button}>Zum Warenkorb <span aria-hidden='true'>→</span></Link>
        </> : <button type='button' className={styles.button}
          disabled={unavailable && !missingAttributes && !inquiry}
          onClick={missingAttributes ? onEdit : onAction}>
          {missingAttributes ? selectionLabel : actionLabel}
        </button>}
      </div>
    </div>
  </div>
}
